import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { apiClient } from '../../utils/apiHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RoomType = () => {
  const [roomTypes, setRoomTypes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ type_name: '', capacity: 1, fee_per_month: 0, description: '' });

  useEffect(() => { fetchRoomTypes(); }, []);

  const fetchRoomTypes = async () => {
    try {
      const response = await apiClient.get('/api/hostel/room-types');
      const data = Array.isArray(response.data) ? response.data : response.data;
      setRoomTypes(data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = editingType ? `/api/hostel/room-types/${editingType.id}` : `/api/hostel/room-types`;
      const response = editingType ? await apiClient.put(url, formData) : await apiClient.post(url, formData);
      if (response.status === 200 || response.data) { alert('Room type saved!'); fetchRoomTypes(); handleCloseModal(); }
    } catch (error) { alert('Failed to save room type'); } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this room type?')) { 
      try {
        await apiClient.delete(`/api/hostel/room-types/${id}`); 
        fetchRoomTypes(); 
        alert('Room type deleted successfully!');
      } catch (error) {
        alert('Failed to delete room type');
      }
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({ type_name: type.type_name, capacity: type.capacity, fee_per_month: type.fee_per_month, description: type.description || '' });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => { setIsModalOpen(false); setEditingType(null); setFormData({ type_name: '', capacity: 1, fee_per_month: 0, description: '' }); };

  return (
    <div className="p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Room Types</CardTitle>
          <Button onClick={() => setIsModalOpen(true)}><Plus className="w-4 h-4 mr-2" />Add Type</Button>
        </CardHeader>
        <CardContent>
          <table className="w-full border-collapse">
            <thead><tr className="border-b"><th className="text-left p-3">Type</th><th className="text-left p-3">Capacity</th><th className="text-left p-3">Fee/Month</th><th className="text-left p-3">Actions</th></tr></thead>
            <tbody>
              {roomTypes.length === 0 ? <tr><td colSpan="4" className="text-center p-4 text-gray-500">No room types found.</td></tr> : roomTypes.map((type) => (
                <tr key={type.id} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium">{type.type_name}</td>
                  <td className="p-3">{type.capacity} persons</td>
                  <td className="p-3">₹{type.fee_per_month}</td>
                  <td className="p-3"><div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => handleEdit(type)}><Edit className="w-3 h-3" /></Button><Button size="sm" variant="destructive" onClick={() => handleDelete(type.id)}><Trash2 className="w-3 h-3" /></Button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingType ? 'Edit' : 'Add'} Room Type</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Type Name *</Label><Input required value={formData.type_name} onChange={(e) => setFormData({ ...formData, type_name: e.target.value })} placeholder="Single, Double, Triple" /></div>
            <div><Label>Capacity *</Label><Input type="number" min="1" required value={formData.capacity} onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })} /></div>
            <div><Label>Fee/Month (₹) *</Label><Input type="number" step="0.01" required value={formData.fee_per_month} onChange={(e) => setFormData({ ...formData, fee_per_month: parseFloat(e.target.value) })} /></div>
            <div><Label>Description</Label><Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button><Button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoomType;